'use client';

import { useTranslation } from '@crehana/i18n';
import type { LANGUAGES } from '@crehana/ts-types';
import { createContext, useContext, useEffect, useRef } from 'react';

import { useTalentAuth } from '@/shared/contexts/talent-auth/talent-auth.client';
import { useUpdatePersonLanguage } from '@/shared/hooks/use-update-person-language';

import { language as cookieLanguage } from '../utils/cookies';
import { getLanguageUser } from '../utils/language';

const TALENT_LANGUAGE_SYNCED_KEY = 'talent_language_synced';

const getLastSyncedLanguage = (): LANGUAGES | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return (
    (window.localStorage.getItem(TALENT_LANGUAGE_SYNCED_KEY) as LANGUAGES) ||
    null
  );
};

const setLastSyncedLanguage = (language: LANGUAGES): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(TALENT_LANGUAGE_SYNCED_KEY, language);
};

type TContext = LANGUAGES;

type TLanguageProviderProps = {
  children: React.ReactNode;
};

const Context = createContext<TContext | undefined>(undefined);

// for talent product, the language value have a different business logic based on the user role and organization configuration
export const CustomLanguageProvider = ({
  children,
}: TLanguageProviderProps) => {
  const { user } = useTalentAuth();
  const { languageCode } = useTranslation();
  const { updatePersonLanguage } = useUpdatePersonLanguage();
  const hasSyncedRef = useRef(false);

  const languageUser: LANGUAGES = getLanguageUser(user) || languageCode;

  // Use the last successfully synced language as the reference baseline instead
  // of the Apollo-cached value, which may reflect a stale server-side cache
  // and not the actual current DB state.
  const lastSyncedLanguage = getLastSyncedLanguage();
  const referenceLanguage = lastSyncedLanguage ?? languageUser;

  const hasCookieDiff = Boolean(
    cookieLanguage && cookieLanguage !== referenceLanguage,
  );
  // Cookie always takes precedence when set, since it is the user's explicit preference
  const finalLanguage: LANGUAGES = cookieLanguage || languageUser;

  useEffect(() => {
    if (!hasSyncedRef.current && hasCookieDiff && user?.email) {
      hasSyncedRef.current = true;
      updatePersonLanguage({
        employee_email: user.email,
        language: cookieLanguage,
        talent_company_id: user.company?.id ?? undefined,
      })
        .then(({ data }) => {
          const result =
            data?.talent.talent_management.update_person_language_admin;
          if (
            result?.__typename === 'PrivateTalentUpdatePersonLanguagePayload'
          ) {
            setLastSyncedLanguage(cookieLanguage);
          } else {
            console.error(
              'Failed to update person language on talent platform',
            );
          }
        })
        .catch(() => {
          console.error('Failed to update person language on talent platform');
        });
    }
  }, [hasCookieDiff, updatePersonLanguage, user?.email, user?.company?.id]);

  return <Context.Provider value={finalLanguage}>{children}</Context.Provider>;
};

export const useLanguage = () => {
  const result = useContext(Context);
  if (typeof result === 'undefined') {
    throw new Error(
      'The useLanguage hook must be used within a LanguageProvider component.',
    );
  }
  return result;
};
